import React, { useEffect, useState, useRef } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import { Batch } from '@/types';
import { BatchService } from '@/services/BatchService';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n/I18nContext';
import { getBrewfatherCredentials } from '@/services/BrewfatherService';
import BatchCard from '@/components/BatchCard';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import SyncIcon from '@mui/icons-material/Sync';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BatchesPage: React.FC = () => {
	const navigate = useNavigate();
	const { user, getAuthHeader, getUserId } = useAuth();
	const { t } = useI18n();
	const [batches, setBatches] = useState<Batch[]>([]);
	const [loading, setLoading] = useState(true);
	const [syncing, setSyncing] = useState(false);
	const [hasCredentials, setHasCredentials] = useState(false);

	useEffect(() => {
		if (!user) {
			navigate('/login');
			return;
		}
		loadBatches();
	}, [user]);

	const loadBatches = async () => {
		try {
			setLoading(true);
			const userId = getUserId();
			if (!userId) {
				setBatches([]);
				return;
			}
			const authHeader = getAuthHeader();
			const credentials = await getBrewfatherCredentials(userId, authHeader);
			if (!credentials) {
				setBatches([]);
				return;
			}
			const response = await BatchService.listBatches(userId, authHeader);
			setBatches(response.items || []);
		} catch (error: any) {
			console.error('Error loading batches:', error);
			toast.error(error.message || t.batches.errorLoading);
			setBatches([]);
		} finally {
			setLoading(false);
		}
	};

	const handleSyncBrewfather = async () => {
		try {
			setSyncing(true);
			await loadBatches();
			toast.success(t.batches.syncSuccess);
		} catch (error: any) {
			console.error('Error syncing batches:', error);
			toast.error(error.message || t.batches.errorSyncing);
		} finally {
			setSyncing(false);
		}
	};

	useEffect(() => {
		const checkCredentials = async () => {
			const userId = getUserId();
			if (!userId) {
				setHasCredentials(false);
				return;
			}
			try {
				const authHeader = getAuthHeader();
				const credentials = await getBrewfatherCredentials(userId, authHeader);
				setHasCredentials(Boolean(credentials));
			} catch {
				setHasCredentials(false);
			}
		};
		checkCredentials();
	}, [user, getUserId, getAuthHeader]);

	if (loading) {
		return (
			<Container sx={{ py: 8, textAlign: 'center' }}>
				<CircularProgress />
			</Container>
		);
	}

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<ToastContainer position="top-right" />
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
				<Typography variant="h4" fontWeight={800}>
					{t.batches.title}
				</Typography>
				{hasCredentials && (
					<Button
						variant="outlined"
						startIcon={<SyncIcon />}
						onClick={handleSyncBrewfather}
						disabled={syncing}
					>
						{syncing ? t.batches.syncing : t.batches.syncBrewfather}
					</Button>
				)}
			</Box>

			{!hasCredentials ? (
				<Paper sx={{ p: 4, textAlign: 'center' }}>
					<Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
						{t.batches.noCredentials}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						{t.batches.noCredentialsDescription}
					</Typography>
					<Button variant="contained" onClick={() => navigate('/dashboard')}>
						{t.batches.goToDashboard}
					</Button>
				</Paper>
			) : batches.length === 0 ? (
				<Paper sx={{ p: 4, textAlign: 'center' }}>
					<Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
						{t.batches.noBatches}
					</Typography>
					<Button variant="outlined" startIcon={<SyncIcon />} onClick={handleSyncBrewfather}>
						{t.batches.syncBrewfather}
					</Button>
				</Paper>
			) : (
				<Grid container spacing={3}>
					{batches.map((batch) => (
						<Grid item xs={12} sm={6} md={4} key={batch.id}>
							<BatchCard
								batch={batch}
								onClick={() => navigate(`/batches/${batch.id}`)}
							/>
						</Grid>
					))}
				</Grid>
			)}
		</Container>
	);
};

export default BatchesPage;
